import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ToolPage, { inputCls, labelCls, primaryBtn, secondaryBtn } from "@/components/ToolPage";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SectionLabel } from "@/components/Eyebrow";
import { ArrowRight } from "lucide-react";

export default function ProbabilityDashboard() {
  const { user } = useAuth();
  const [stage, setStage] = useState("form");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    current_college: "Valencia College", credits_completed: 30, aa_status: "in_progress",
    current_gpa: 3.7, intended_major: "Economics", target_school: "UF",
    prereqs_completed_count: 4, prereqs_total_estimated: 8,
    strengths: "Strong macroeconomics coursework; Phi Theta Kappa officer.",
    weaknesses: "No quantitative competitions yet.",
    activities: "Founded campus financial-literacy club; tutor.",
  });
  const [email, setEmail] = useState(""); const [name, setName] = useState("");
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const next = (e) => { e.preventDefault(); setStage(user ? "running" : "gate"); if (user) run(); };

  const run = async (e) => {
    if (e) e.preventDefault();
    if (!user && (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) { toast.error("Please enter a valid email."); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        credits_completed: parseInt(form.credits_completed, 10) || 0,
        current_gpa: parseFloat(form.current_gpa) || 0,
        prereqs_completed_count: parseInt(form.prereqs_completed_count, 10) || 0,
        prereqs_total_estimated: parseInt(form.prereqs_total_estimated, 10) || 1,
        email: email || undefined, name: name || undefined,
      };
      const res = await api.post("/tools/probability-dashboard", payload);
      setResult(res.data); setStage("result");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tool run failed.");
      if (err?.response?.status === 429) setStage("form");
    } finally { setLoading(false); }
  };

  return (
    <ToolPage
      numeral="IV"
      name="Transfer Probability Dashboard"
      blurb="A composite readiness signal across six admissions dimensions. Not an admissions prediction."
      seoTitle="Transfer Probability Dashboard — Six-Dimension Readiness | Beyond Valencia"
      seoDescription="A composite six-dimension readiness signal for Florida community college transfer applicants. GPA, prereqs, narrative, activities, risk factors — all in one dashboard."
      seoCanonical="/tools/probability-dashboard"
      seoKeywords="transfer probability, admissions probability, transfer readiness, UF admission chance, Ivy transfer probability"
    >
      {stage === "form" && (
        <div className="border border-stone/40 bg-bone p-6 sm:p-10 max-w-3xl">
          <SectionLabel index="A" label="Intake" />
          <form onSubmit={next} className="mt-7 space-y-5" data-testid="probdash-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Current college</Label><Input className={inputCls} value={form.current_college} onChange={set("current_college")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Credits completed</Label><Input type="number" className={inputCls} value={form.credits_completed} onChange={set("credits_completed")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>AA status</Label>
                <select className={`${inputCls} w-full h-9 px-3 text-sm`} value={form.aa_status} onChange={set("aa_status")}><option value="not_started">Not started</option><option value="in_progress">In progress</option><option value="completed">Completed</option></select>
              </div>
              <div className="space-y-2"><Label className={labelCls}>Current GPA</Label><Input className={inputCls} value={form.current_gpa} onChange={set("current_gpa")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Intended major</Label><Input className={inputCls} value={form.intended_major} onChange={set("intended_major")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Target school</Label><Input className={inputCls} value={form.target_school} onChange={set("target_school")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Prereqs completed</Label><Input type="number" className={inputCls} value={form.prereqs_completed_count} onChange={set("prereqs_completed_count")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Total prereqs (est.)</Label><Input type="number" className={inputCls} value={form.prereqs_total_estimated} onChange={set("prereqs_total_estimated")} required /></div>
            </div>
            <div className="space-y-2"><Label className={labelCls}>Strengths</Label><Textarea className={inputCls} rows={2} value={form.strengths} onChange={set("strengths")} /></div>
            <div className="space-y-2"><Label className={labelCls}>Weaknesses</Label><Textarea className={inputCls} rows={2} value={form.weaknesses} onChange={set("weaknesses")} /></div>
            <div className="space-y-2"><Label className={labelCls}>Activities</Label><Textarea className={inputCls} rows={2} value={form.activities} onChange={set("activities")} /></div>
            <button type="submit" className={primaryBtn} data-testid="probdash-next-btn">Continue</button>
          </form>
        </div>
      )}

      {stage === "gate" && (
        <div className="border border-stone/40 bg-bone p-6 sm:p-10 max-w-2xl" data-testid="probdash-gate">
          <SectionLabel index="B" label="One step before we run it" />
          <form onSubmit={run} className="mt-7 space-y-5">
            <div className="space-y-2"><Label className={labelCls}>First name</Label><Input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="space-y-2"><Label className={labelCls}>Email</Label><Input type="email" data-testid="probdash-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div className="flex gap-3"><button type="submit" disabled={loading} className={primaryBtn} data-testid="probdash-submit-btn">{loading ? "Reading…" : "Run my dashboard"}</button><button type="button" onClick={() => setStage("form")} className={secondaryBtn}>Back</button></div>
          </form>
        </div>
      )}

      {(stage === "running" || loading) && stage !== "result" && (<div className="text-ink-soft">Computing readiness…</div>)}

      {stage === "result" && result && (
        <div className="space-y-10" data-testid="probdash-result">
          <div className="grid sm:grid-cols-2 gap-0 border-t border-l border-stone/40">
            <div className="border-r border-b border-stone/40 bg-bone-dim p-10 text-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Readiness</div>
              <div className="font-serif font-medium text-[8rem] leading-none text-ivy tabular-nums mt-3" data-testid="probdash-score">{result.readiness_score}</div>
              <div className="font-serif text-xl text-ink tracking-tight mt-1">{result.readiness_label}</div>
            </div>
            <div className="border-r border-b border-stone/40 bg-ivy text-bone p-10 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Recommended next step</div>
              <p className="mt-4 font-serif text-xl leading-snug tracking-tight">{result.recommended_next_step}</p>
              <Link to="/transfer-audit" className="mt-6 inline-flex items-center gap-2 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-xs font-semibold py-3.5 px-6 transition-colors w-fit">Human audit ($249) <ArrowRight className="h-3 w-3" /></Link>
            </div>
          </div>
          <div className="border border-stone/40 bg-bone p-7" data-testid="probdash-dimensions">
            <SectionLabel index="06" label="Six dimensions" />
            <div className="mt-7 space-y-5">
              {(result.dimensions || []).map((d) => {
                const isRisk = d.name === "Risk Level";
                // Risk: invert — longer bar = more risk = worse.
                const widthPct = (d.score / 10) * 100;
                return (
                  <div key={d.name} className="border-b border-stone/30 pb-4 last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-baseline gap-3">
                      <div className="font-serif text-lg text-ink tracking-tight">{d.name}{isRisk && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-stone">↑ more risk</span>}</div>
                      <div className="font-mono text-sm tabular-nums text-brass">{d.score}/10</div>
                    </div>
                    <div className="mt-3 relative h-[3px] bg-stone/30 overflow-hidden">
                      <div className={`absolute inset-y-0 left-0 ${isRisk ? "bg-brass" : "bg-ivy"}`} style={{ width: `${widthPct}%` }} />
                    </div>
                    <p className="text-sm text-ink-soft mt-3 leading-relaxed">{d.comment}</p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-0 border-t border-l border-stone/40">
            <div className="border-r border-b border-stone/40 bg-bone p-6"><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Strongest</div><p className="font-serif text-xl text-ink mt-2 tracking-tight">{result.strongest_dimension}</p></div>
            <div className="border-r border-b border-stone/40 bg-bone p-6"><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Weakest</div><p className="font-serif text-xl text-ink mt-2 tracking-tight">{result.weakest_dimension}</p></div>
          </div>
          <p className="text-[11px] text-stone italic">{result.disclaimer}</p>
        </div>
      )}
    </ToolPage>
  );
}
